export interface ToyDot { x: number; velocity: number; impact: number }

export function nudgeDot(dot: ToyDot, direction: number): void {
  dot.velocity = Math.max(-1200, Math.min(1200, dot.velocity + direction * 950))
}

export function stepDots(dots: ToyDot[], elapsed: number, bound: number): boolean {
  const steps = Math.ceil(Math.min(elapsed, 0.05) / (1 / 120))
  const dt = steps ? Math.min(elapsed, 0.05) / steps : 0
  for (let step = 0; step < steps; step++) {
    dots.forEach((dot, index) => {
      const home = index === 0 ? -48 : 48
      dot.velocity += (-65 * (dot.x - home) - 5 * dot.velocity) * dt
      dot.x += dot.velocity * dt
      dot.impact *= Math.exp(-14 * dt)
      if (Math.abs(dot.x) > bound) {
        dot.x = Math.sign(dot.x) * bound
        dot.velocity = -Math.sign(dot.x) * Math.abs(dot.velocity) * 0.65
      }
    })
    const [left, right] = dots
    if (right.x - left.x < 24) {
      const middle = Math.max(-bound + 12, Math.min(bound - 12, (left.x + right.x) / 2))
      left.x = middle - 12
      right.x = middle + 12
      if (left.velocity > right.velocity) {
        const relative = left.velocity - right.velocity
        const impulse = relative * 0.9
        left.velocity -= impulse
        right.velocity += impulse
        left.impact = right.impact = Math.min(1, relative / 600)
      }
    }
  }
  return dots.some((dot, index) => Math.abs(dot.x - (index === 0 ? -48 : 48)) > 0.15
    || Math.abs(dot.velocity) > 0.5 || dot.impact > 0.01)
}

export function createLoadingToy(button: HTMLButtonElement) {
  const elements = [...button.querySelectorAll<HTMLElement>('.meet-dot')]
  const label = button.querySelector<HTMLElement>('.meet-toy-label')!
  const motion = matchMedia('(prefers-reduced-motion: reduce)')
  let reduced = motion.matches
  const dots: ToyDot[] = [{ x: -48, velocity: 0, impact: 0 }, { x: 48, velocity: 0, impact: 0 }]
  let active = false
  let frame = 0
  let previous = 0

  function render() {
    elements.forEach((element, index) => {
      const dot = dots[index]
      element.style.transform = `translateX(${dot.x - (index === 0 ? -48 : 48)}px) scale(${1 - dot.impact * 0.16}, ${1 + dot.impact * 0.16})`
    })
  }

  function reset() {
    cancelAnimationFrame(frame)
    frame = previous = 0
    dots.forEach((dot, index) => Object.assign(dot, { x: index === 0 ? -48 : 48, velocity: 0, impact: 0 }))
    delete button.dataset.nudged
    label.textContent = ''
    render()
  }

  function tick(now: number) {
    frame = 0
    const moving = stepDots(dots, previous ? (now - previous) / 1000 : 0, Math.max(60, button.clientWidth / 2 - 25))
    previous = now
    render()
    if (moving) frame = requestAnimationFrame(tick)
    else reset()
  }

  function nudge(indices: number[]) {
    if (!active || document.hidden) return
    syncMotion()
    if (reduced) {
      button.dataset.nudged = indices.length === 2 ? 'both' : indices[0] === 0 ? 'green' : 'orange'
      label.textContent = indices.length === 2 ? 'Both dots nudged' : indices[0] === 0 ? 'Green dot nudged' : 'Orange dot nudged'
      return
    }
    indices.forEach((index) => nudgeDot(dots[index], index === 0 ? 1 : -1))
    if (!frame) {
      previous = 0
      frame = requestAnimationFrame(tick)
    }
  }

  button.addEventListener('click', (event) => {
    const x = event.clientX - button.getBoundingClientRect().left - button.clientWidth / 2
    nudge(event.detail === 0 ? [0, 1] : [Math.abs(x - dots[0].x) <= Math.abs(x - dots[1].x) ? 0 : 1])
  })
  button.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    event.preventDefault()
    nudge([event.key === 'ArrowLeft' ? 0 : 1])
  })
  function syncMotion() {
    if (reduced === motion.matches) return
    reduced = motion.matches
    reset()
  }
  motion.addEventListener('change', syncMotion)
  document.addEventListener('visibilitychange', () => { if (document.hidden) reset() })
  return { setActive(value: boolean) { if (active !== value) reset(); active = value } }
}
